import $ from 'jquery'
import _ from 'lodash'
import arrayToHash from 'scoreboard/components/array-to-hash'
import i18n from 'scoreboard/components/i18n'
import selects from 'scoreboard/components/selects'
import Sorter from 'scoreboard/components/sorter'
import Dataset from 'scoreboard/datasets/model/dataset'
const { BLANK } = Dataset
import formatValue from 'scoreboard/components/format-value'
import renderYesNoSelect from 'scoreboard/datasets/filters/render-yes-no-select'
import { getNumericFormatValueOptions } from 'scoreboard/datasets/filters/filter-numeric-values'
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
import { getPrecisionOrDefault } from 'scoreboard/components/format-value'
import DataType from 'scoreboard/datasets/model/data-type'
const { CURRENCY, TEXT, YES_NO, CLUSTER, ROLLUP } = DataType
import isGeographicOrRollup from 'scoreboard/datasets/is-geographic-or-rollup'

/**
 * Turns a given <select> into a filtering select appropriate for text filters
 *
 * @param {jQuery} el - element to turn into a filtering select
 *
 * @param {Array} [values] - initially selected values
 *
 * @param {Array} [equations] - list of equations that have already been entered
 *
 * @param {Array} [suggestions] values that should be presented as suggestions
 *
 * @param {Sorter} [sorter=new Sorter({caseSensitive: false})] - provide a custom sorter
 * Default is a case insensitive ascending (text) sort
 *
 * @param {String} operator - the filter's selected operator
 *
 * @param {Object} [selectOptions] - options that should be passed on to scoreboard-select
 *
 * @param {String} dataType
 *
 * @param {Number} precision the precision to round numeric values to
 *
 * @param {String} [currencyCode] the currency code to use
 *
 * @param {Dataset} [dataset]
 *
 * @param {Number} [fieldId] for datasets, the ID of the field whose values are
 * being rendered by this select
 *
 * @param {Function} [valueFormatter] If the value to be displayed is different
 * from the backing, model value, this can be provided to map the model
 * representation to a label value.
 * @param {Function} [valueConverter] Intended to be used with valueFormatter.
 * Converts new/manually-entered values from the display format to the model
 * representation.
 */
function renderValuesSelect ({ el, values, equations, suggestions = [], operator,
	sorter, selectOptions = {}, dataType, precision, currencyCode, dataset, fieldId,
	valueFormatter, valueConverter
}) {
	// #37627 render the yesNoSelect if that's the dataType we passed in
	if (dataType === YES_NO) {
		renderYesNoSelect(el, _.extend({}, selectOptions, {
			selected: values
		}))
		return
	}
	// #42615 make sure currency values are passed to selects.filtering as strings
	if (dataType === CURRENCY) {
		values = _.map(values, value => value.toString())
	}

	if (_.isNil(precision)) {
		precision = getPrecisionOrDefault(precision, dataType)
	}

	let suggestionsToUse = null
	if (_.includes(['is-any', 'is-not-any', 'is'], operator)) {
		suggestionsToUse = suggestions
		if (_.includes(['is-any', 'is-not-any'], operator)) {
			suggestionsToUse = [ BLANK ].concat(suggestionsToUse)
		}
	}
	if (suggestionsToUse) {
		sorter = sorter || new Sorter({caseSensitive: false})
		suggestionsToUse = sorter.sort(suggestionsToUse).results
	}

	let defaults = selects.defaults()
	let blankOption = `<span class="blank-value">${i18n.get('common.blank')}</span>`

	// If the select is not visible when we render it, it will likely come out with
	// sizing problems https://github.com/SpiderStrategies/Scoreboard/issues/26362
	// so make sure it's visible.
	$(el).removeClass('is-not-displayed')

	const assignLabel = option => option.label = valueFormatter ?
		valueFormatter(option.value) :
			label(option, dataType, precision, dataset, fieldId, currencyCode)

	const optionTransformerFactory = (baseMethod) =>
		(option, input) => {
			if (option.value === BLANK) {
				return blankOption
			} else {
				assignLabel(option)
				return baseMethod(option, input)
			}
		}

	selects.filtering(el, _.extend({
		multiple: true,
		sort: [],
		render: {
			item: optionTransformerFactory(defaults.render.item),
			option: optionTransformerFactory(defaults.render.option),
			create: function (option) {
				// The create new option at the bottom
				if (valueConverter) {
					option.value = valueConverter(option.value)
				}
				assignLabel(option)
				return i18n.get('common.add2', option.label)
			}
		},
		options: options({
			suggestions: suggestionsToUse,
			values,
			equations })
	}, selectOptions))
}

function label(d, dataType, precision, dataset, fieldId, currencyCode) {
	if (dataType === CLUSTER) {
		return dataset.getClusterLabel(fieldId, d.value)
	} else if (isGeographicOrRollup(dataType)) {
		// #45992 no formatting for rollup field labels; even if they consist
		// entirely of numbers, they are labels, not numeric values
		// #61288 #61289 no need to format geographic field labels either
		return d.value ?? d.label
	}

	const options = {
		value: d.value,
		dataType,
		precision,
		currencyCode
	}
	if (isNumericDataType(dataType)) {
		_.extend(options, getNumericFormatValueOptions(options.value, {
			dataType,
			precision,
			currencyCode
		}))
	}

	let formatted = dataType ? formatValue(options) : d.label
	// #51880 dataset value could contain reserved HTML characters, or even
	// actual HTML, so escape it
	return _.escape(formatted)
}

/**
 * Returns the options that should be in a multi-select for a text filter
 *
 * @param {Array} [suggestions] values that should be presented as suggestions
 *
 * @param {Array} [values] - initially selected values. Assumed to be escaped.
 *
 * @param {Array} [equations] - list of equations that have already been entered
 *
 * @returns {Array} of options suitable for sending to the select, with
 * label, value and selected properties
 */
function options ({ suggestions, values, equations }) {
	var options = values ? _.clone(values) : []
	// #17440 don't keep empty options around
	options = _.filter(options, option => !_.isNil(option))
	var selectedHash = arrayToHash(options)
	if (!_.isEmpty(suggestions)) {
		_.each(suggestions, function(suggestion) {
			if (!selectedHash[suggestion]) {
				options.push(suggestion)
			}
		})
	}
	return _.map(options, function(value) {
		return {
			label: value,
			value: value,
			selected: selectedHash[value],
			dataset: {
				type: _.includes(equations, value) ? 'equation' : TEXT
			}
		}
	})
}

export default renderValuesSelect
export { label }

renderValuesSelect.__filename__ = 'datasets/filters/render-values-select.js'
